import React, { memo, useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';

import { AGGREGATED_FIELD_STATE } from '../../../../store/products/helpers/aggregatedFieldState';
import localization from '../../../../shared/strings';
import ProductStatusSelect, { MIXED_STATUS_ID } from '../../ProductStatusSelect';
import { useLocalFieldValue } from '../../hooks';
import { aggregatedStringFieldPropType } from '../helpers/aggregatedFieldPropTypes';
import { resolveAggregatedStatusValue } from '../helpers/resolveAggregatedStatusValue';
import { useBindAggregatedField } from '../hooks/useBindAggregatedField';
import styles from './AggregatedProductStatus.module.scss';

const l10n = localization.PRODUCT_INFO;
const aggregatedL10n = l10n.aggregated;

const AggregatedProductStatus = ({
  statusField,
  includeArchived,
  readOnly,
  readOnlyTooltip,
}) => {
  const bindings = useBindAggregatedField('status', statusField, l10n.statusLabel);
  const resolvedStatus = resolveAggregatedStatusValue(statusField);
  const isMultiple = statusField?.state === AGGREGATED_FIELD_STATE.MULTIPLE;
  const { localValue, setLocalValue } = useLocalFieldValue(resolvedStatus);

  useEffect(() => {
    setLocalValue(resolvedStatus);
  }, [resolvedStatus, setLocalValue]);

  const handleChange = useCallback((nextStatus) => {
    if (readOnly) return;
    if (nextStatus === MIXED_STATUS_ID) return;
    if (nextStatus === localValue) return;

    const previous = localValue;
    setLocalValue(nextStatus);
    bindings.onChange(nextStatus, () => setLocalValue(previous));
  }, [bindings, localValue, readOnly, setLocalValue]);

  return (
    <div className={styles.wrap}>
      <ProductStatusSelect
        productStatus={localValue}
        includeArchived={includeArchived}
        mixedValuesLabel={isMultiple ? aggregatedL10n.mixedValues : null}
        readOnly={readOnly}
        readOnlyTooltip={readOnlyTooltip}
        onChange={handleChange}
      />
    </div>
  );
};

AggregatedProductStatus.propTypes = {
  statusField: aggregatedStringFieldPropType,
  includeArchived: PropTypes.bool,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
};

AggregatedProductStatus.defaultProps = {
  statusField: null,
  includeArchived: false,
  readOnly: false,
  readOnlyTooltip: null,
};

export default memo(AggregatedProductStatus);
